import React from 'react';

export default function SecurityPolicy() {
  return (
    <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter']">
      <main className="pt-16 pb-20 bg-[#111010]">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-[#363232] border border-[#504A4A] rounded-lg p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-center text-[#EBFFEE]">
              Vulnerability Disclosure Policy
            </h1>
            <p className="text-center text-[#A9A3A3] mb-8">RollOps LLC</p>
            <p className="text-center text-[#A9A3A3] mb-12">
              <strong>Effective Date:</strong> April 28, 2026
            </p>

            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-[#A9A3A3] mb-8 leading-relaxed">
                RollOps LLC (&quot;RollOps,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) values the work of independent
                security researchers in helping us keep our gym management platform safe for the academies,
                instructors, and students who depend on it. This policy describes how to report a suspected
                vulnerability, what we commit to in return, and the boundaries of testing we authorize.
              </p>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">I. SCOPE</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  This policy applies to vulnerabilities discovered in any of the following:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-6 space-y-1">
                  <li>The RollOps web application (rollops.pro and its subdomains)</li>
                  <li>The RollOps mobile applications (iOS and Android)</li>
                  <li>RollOps Academy properties (rollops.academy and its subdomains)</li>
                  <li>Public APIs and edge workers operated by RollOps</li>
                </ul>
                <p className="text-[#A9A3A3] leading-relaxed">
                  Third-party services we integrate with (e.g., Supabase, Stripe, Cloudflare, Google) are
                  out of scope. Please report issues in those services directly to the vendor.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">II. HOW TO REPORT</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  Email a detailed report to{" "}
                  <a href="mailto:security@rollops.pro" className="text-rollops-accent hover:underline">
                    security@rollops.pro
                  </a>
                  . To help us reproduce and triage quickly, please include:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-6 space-y-1">
                  <li>A description of the vulnerability and its potential impact</li>
                  <li>Step-by-step reproduction instructions, including any required accounts or tooling</li>
                  <li>Affected URLs, endpoints, or app screens</li>
                  <li>Proof-of-concept code, screenshots, or video where applicable</li>
                  <li>Your name or handle for acknowledgement (optional)</li>
                </ul>
                <p className="text-[#A9A3A3] leading-relaxed">
                  We accept reports in English. If you wish to encrypt your report, request our PGP key in
                  your initial email and we will provide one.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">III. WHAT WE COMMIT TO</h2>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-4 space-y-2">
                  <li>
                    <strong>Acknowledgement</strong> within 3 business days of receiving your report.
                  </li>
                  <li>
                    <strong>Triage and severity assessment</strong> within 10 business days, with a target
                    remediation timeline shared back to you.
                  </li>
                  <li>
                    <strong>Status updates</strong> at meaningful milestones until the issue is resolved.
                  </li>
                  <li>
                    <strong>Credit</strong> in our acknowledgements page, if you would like to be named.
                  </li>
                  <li>
                    <strong>No legal action</strong> against researchers acting in good faith and within the
                    boundaries of this policy.
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">IV. SAFE-HARBOR RULES OF ENGAGEMENT</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  Testing is authorized only when conducted in accordance with the following rules. Testing
                  outside these boundaries is not authorized and may be considered a violation of the
                  Computer Fraud and Abuse Act or comparable law.
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-6 space-y-2">
                  <li>Do not access, modify, or delete data belonging to other users.</li>
                  <li>
                    Do not run denial-of-service, traffic-flooding, or volumetric tests against production
                    systems.
                  </li>
                  <li>
                    Do not attempt social engineering, phishing, or physical attacks against RollOps employees,
                    contractors, or our gym customers.
                  </li>
                  <li>
                    Use only accounts you own or are explicitly authorized to test with. If you create an
                    account for testing, please use a clearly identifiable test email.
                  </li>
                  <li>
                    Stop testing and report immediately if you encounter customer personal data, financial
                    data, or any other sensitive information.
                  </li>
                  <li>
                    Do not publicly disclose the vulnerability before we have had a reasonable opportunity to
                    remediate it (we target 90 days from acknowledgement, sooner where possible).
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">V. OUT-OF-SCOPE FINDINGS</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  The following are typically not eligible for acknowledgement:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-4 space-y-1">
                  <li>Reports based solely on automated scanner output without demonstrated impact</li>
                  <li>Missing security headers without a working exploit chain</li>
                  <li>Issues requiring physical access to a victim&apos;s unlocked device</li>
                  <li>Self-XSS or issues requiring the victim to disable security controls</li>
                  <li>Rate-limit or brute-force concerns without a concrete impact</li>
                  <li>Vulnerabilities in third-party libraries without a working RollOps-side exploit</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">VI. CONTACT</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  Questions about this policy or the disclosure process can be sent to the same address used
                  for reports:
                </p>
                <div className="bg-[#111010] border border-[#504A4A] rounded p-4">
                  <p className="text-[#EBFFEE] font-semibold mb-2">RollOps LLC</p>
                  <p className="text-[#A9A3A3] mb-2">
                    Email:{" "}
                    <a href="mailto:security@rollops.pro" className="text-rollops-accent hover:underline">
                      security@rollops.pro
                    </a>
                  </p>
                  <p className="text-[#A9A3A3]">Mailing Address: 1908 Thomes Ave STE 12408 Cheyenne, WY 82001</p>
                </div>
              </section>

              <div className="text-center pt-8 border-t border-[#504A4A]">
                <p className="text-[#A9A3A3]">This Vulnerability Disclosure Policy was last updated on April 28, 2026.</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
